import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage } from "react-use";
import { useDraft } from "~/components/Hooks/useDraft";
import { fetchUserConversations } from "~/store/actions/actions.conversations";
import { fetchConversations } from "~/store/actions/actions.conversations";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { fetchPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getInstanceId } from "~/store/selectors/selector.instance";
import { canAccessChat, getLoggedUserId } from "~/store/selectors/selector.users";
import { useNetworkStateChange } from "../Hooks/useNetworkStateChange";

export const useChat = (rememberLastSelectedConversation) => {
  const [draft, setDraft] = useDraft(`conversation_message_draft`);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  const canUserAccessChat = useSelector(canAccessChat);
  const instanceId = useSelector(getInstanceId);
  const loggedUserId = useSelector(getLoggedUserId);
  const localStoragePrefix = instanceId
    ? "instance_" + `${instanceId}` + "_user_" + `${loggedUserId}` + "_"
    : "user_" + `${loggedUserId}` + "_";

  const [lastVisitedConversationId, setLastVisitedConversation] = useLocalStorage(
    localStoragePrefix + "last_visited_conversation_id",
    0
  );

  const [conversationId, setCurrentConversationId] = useState(
    rememberLastSelectedConversation ? lastVisitedConversationId : 0
  );

  const setConversationId = useCallback(
    (newActiveConversationId) => {
      setLastVisitedConversation(newActiveConversationId);
      setCurrentConversationId(newActiveConversationId);
    },
    [setLastVisitedConversation]
  );

  const defaultMessagesSound = useSelector((state) => {
    return getRawPersonalizedConfigOption(state, `chat_messages_sound_enabled`);
  });

  const defaultChatShortcuts = useSelector((state) => {
    return getRawPersonalizedConfigOption(state, `chat_shortcuts_enabled`);
  });

  const onToggleMessagesSound = useCallback(
    (value) => {
      dispatch(editPersonalizedConfigOptions({ chat_messages_sound_enabled: !value }));
    },
    [dispatch]
  );

  const onToggleChatShortcuts = useCallback(
    (value) => {
      dispatch(editPersonalizedConfigOptions({ chat_shortcuts_enabled: !value }));
    },
    [dispatch]
  );

  const getConversationsData = useCallback(async () => {
    await dispatch(fetchPersonalizedConfigOptions("chat_messages_sound_enabled", "chat_shortcuts_enabled"));
    await dispatch(fetchUserConversations());
    await dispatch(fetchConversations());
  }, [dispatch]);

  useNetworkStateChange(canUserAccessChat ? getConversationsData : () => null);

  React.useEffect(() => {
    if (canUserAccessChat) {
      getConversationsData().then(() => {
        setLoading(false);
      });
    }
  }, [canUserAccessChat, getConversationsData]);

  return {
    defaultMessagesSound,
    defaultChatShortcuts,
    loading,
    conversationId,
    setConversationId,
    onToggleMessagesSound,
    onToggleChatShortcuts,
    draft,
    setDraft,
  };
};
